/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon, DocumentIcon, UploadIcon, MapIcon, QuestionMarkCircleIcon } from "@heroicons/react/solid";
import { EO_TYPE_OF_QUESTION_DROPDOWN_OPTIONS } from "../../../constants/constants";
import MultipleChoiceQuestion from "../../ui/MultipleChoiceQuestion";

const CreateEventContent4 = ({
  handleContinue,
  handlePrevious,
  handleSaveForLater,
  handleUploadClick,
  eventData,
  updateEventData,
  saveEventData,
  formState,
  setFormState,
  handleEventDocsUpload
}) => {
  const { t } = useTranslation();
  const typeOfQuestionsDropdownOptions = EO_TYPE_OF_QUESTION_DROPDOWN_OPTIONS;
  const [dropdownOpenStates, setDropdownOpenStates] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    map: false,
    questions: false,
    faq: false,
    documents: false
  });

  useEffect(() => {
    if (!formState) return;
    setFormState(prev => ({
      ...prev,
      typeOfQuestionList: Array.isArray(prev?.typeOfQuestionList) ? prev.typeOfQuestionList : [{}],
      faqList: Array.isArray(prev?.faqList) ? prev.faqList : [{ question: "", answer: "" }],
      eventDocuments: Array.isArray(prev?.eventDocuments) ? prev.eventDocuments : []
    }));
  }, []);

  useEffect(() => {
    if (!eventData) return;
    setFormState(prev => ({
      ...prev,
      typeOfQuestionList: Array.isArray(eventData?.typeOfQuestionList) ? eventData.typeOfQuestionList : [{}],
      faqList: Array.isArray(eventData?.faqList) ? eventData.faqList : [{ question: "", answer: "" }],
      eventDocuments: Array.isArray(eventData?.eventDocuments) ? eventData.eventDocuments : []
    }));
  }, [eventData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFileRemove = (index) => {
    if (!formState?.eventDocuments) return;
    setFormState((prev) => ({
      ...prev,
      eventDocuments: prev.eventDocuments.filter((_, i) => i !== index),
    }));
  };

  const toggleDropdown = (index) => {
    setDropdownOpenStates((prevState) => {
      const newState = [...prevState];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const handleTypeofQuestionOptionChange = (option, index) => {
    setFormState((prev) => {
      const updatedList = [...prev.typeOfQuestionList];
      updatedList[index] = option;
      return { ...prev, typeOfQuestionList: updatedList };
    });
    setDropdownOpenStates((prevState) => {
      const newState = [...prevState];
      newState[index] = false;
      return newState;
    });
  };

  const renderTypeOfQuestionContent = (index) => {
    const type = formState?.typeOfQuestionList[index].value;
    const questionData = formState?.typeOfQuestionList[index];

    const handleQuestionChange = (index, updatedQuestion) => {
      setFormState((prev) => {
        const updatedList = [...prev.typeOfQuestionList];
        updatedList[index] = updatedQuestion;
        return { ...prev, typeOfQuestionList: updatedList };
      });
    };

    switch (type) {
      case "multipleChoice":
        return (
          <MultipleChoiceQuestion
            index={index}
            questionData={questionData}
            onQuestionChange={handleQuestionChange}
          />
        );
      case "shortAnswer":
      case "longAnswer":
        return (
          <div className="mt-4">
            <input
              type="text"
              value={formState?.typeOfQuestionList[index].questionTitle || ""}
              onChange={(e) => {
                const updatedQuestionList = [...formState.typeOfQuestionList];
                updatedQuestionList[index] = {
                  ...updatedQuestionList[index],
                  questionTitle: e.target.value,
                };
                setFormState((prev) => ({ ...prev, typeOfQuestionList: updatedQuestionList }));
              }}
              placeholder="Enter your question..."
              className="w-full px-4 py-3 text-sm border border-grey-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-grey-300 focus:border-transparent"
            />
          </div>
        );
      default:
        return null;
    }
  };

  const addNewTypeOfQuestion = () => {
    setFormState((prev) => ({
      ...prev,
      typeOfQuestionList: [...prev.typeOfQuestionList, ""],
    }));
    setDropdownOpenStates([...dropdownOpenStates, false]);
  };

  const handleNewFAQInputChange = (index, event) => {
    const { name, value } = event.target;
    const updatedFaqList = [...formState.faqList];
    updatedFaqList[index][name] = value;
    setFormState((prev) => ({ ...prev, faqList: updatedFaqList }));
  };

  const addNewFAQ = () => {
    setFormState((prev) => ({
      ...prev,
      faqList: [...formState.faqList, { question: "", answer: "" }],
    }));
  };

  const handleDeleteQuestion = (index) => {
    const updatedTypeOfQuestionList = formState?.typeOfQuestionList.filter((_, i) => i !== index);
    setFormState((prev) => ({ ...prev, typeOfQuestionList: updatedTypeOfQuestionList }));
  };

  const handleDeleteFAQ = (index) => {
    const updatedFaqList = formState?.faqList.filter((_, i) => i !== index);
    setFormState((prev) => ({ ...prev, faqList: updatedFaqList }));
  };

  const handleSaveContinue = () => {
    updateEventData(formState);
    handleContinue();
  };

  const handleSaveForLaterClick = () => {
    updateEventData({
      eventId: eventData.eventId,
      activeStep: 3,
      formState,
    });
    handleSaveForLater();
  };

  return (
    <div className="w-full min-h-full bg-white">
      <div className="max-w-2xl mx-auto px-6 py-12">
        
        {/* Back Button */}
        <button
          onClick={handlePrevious}
          className="flex items-center gap-1 text-grey-400 hover:text-grey-600 text-sm mb-12 transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          <span>{t('common.back')}</span>
        </button>

        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-3xl font-semibold text-grey-900 tracking-tight">
            {t('createEvent.additionalDetails')}
          </h1>
          <p className="text-grey-400 mt-3 text-lg">
            {t('createEvent.additionalDetailsSubtitle')}
          </p>
        </div>

        {/* Collapsible Sections */}
        <div className="space-y-4">
          
          {/* Event Map Section */}
          <div className="border border-grey-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => toggleSection('map')}
              className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-grey-25 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-grey-100 rounded-full flex items-center justify-center">
                  <MapIcon className="h-5 w-5 text-grey-500" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-grey-900">{t('createEvent.eventMap')}</p>
                  <p className="text-sm text-grey-400">{t('createEvent.eventMapSubtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {formState?.eventMap && (
                  <span className="text-xs font-medium text-grey-600 bg-grey-100 px-2 py-1 rounded-full">{t('createEvent.added')}</span>
                )}
                {expandedSections.map ? (
                  <ChevronUpIcon className="h-5 w-5 text-grey-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-grey-400" />
                )}
              </div>
            </button>
            
            {expandedSections.map && (
              <div className="px-6 py-6 border-t border-grey-100 bg-grey-25">
                <button
                  onClick={handleUploadClick}
                  className="w-full py-8 border-2 border-dashed border-grey-200 rounded-xl hover:border-grey-300 transition-colors flex flex-col items-center gap-2"
                >
                  <UploadIcon className="h-8 w-8 text-grey-300" />
                  <span className="text-sm font-medium text-grey-600">{t('createEvent.uploadEventMap')}</span>
                  <span className="text-xs text-grey-400">{t('createEvent.uploadFileTypes')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Vendor Questions Section */}
          <div className="border border-grey-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => toggleSection('questions')}
              className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-grey-25 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-grey-100 rounded-full flex items-center justify-center">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-grey-500" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-grey-900">{t('createEvent.applicationQuestions')}</p>
                  <p className="text-sm text-grey-400">{t('createEvent.applicationQuestionsSubtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(formState?.typeOfQuestionList || []).filter(q => q?.value).length > 0 && (
                  <span className="text-xs font-medium text-grey-600 bg-grey-100 px-2 py-1 rounded-full">
                    {formState.typeOfQuestionList.filter(q => q?.value).length} added
                  </span>
                )}
                {expandedSections.questions ? (
                  <ChevronUpIcon className="h-5 w-5 text-grey-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-grey-400" />
                )}
              </div>
            </button>
            
            {expandedSections.questions && (
              <div className="px-6 py-6 border-t border-grey-100 bg-grey-25 space-y-4">
                {(formState?.typeOfQuestionList || []).map((selectedOption, index) => (
                  <div key={index} className="bg-white rounded-xl border border-grey-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">
                        Question {index + 1}
                      </span>
                      {index > 0 && (
                        <button
                          onClick={() => handleDeleteQuestion(index)}
                          className="text-grey-400 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="relative">
                      <button
                        onClick={() => toggleDropdown(index)}
                        className="w-full px-4 py-3 text-left bg-grey-50 border border-grey-200 rounded-xl flex items-center justify-between hover:border-grey-300 transition-colors"
                      >
                        <span className={selectedOption?.label ? "text-grey-900" : "text-grey-400"}>
                          {selectedOption?.label || t('createEvent.selectQuestionType')}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 text-grey-400" />
                      </button>
                      
                      {dropdownOpenStates[index] && (
                        <div className="absolute z-20 mt-2 w-full bg-white border border-grey-200 rounded-xl shadow-lg overflow-hidden">
                          {typeOfQuestionsDropdownOptions.map((option) => (
                            <button
                              key={option.value}
                              className="w-full px-4 py-3 text-left hover:bg-grey-50 transition-colors border-b border-grey-100 last:border-0"
                              onClick={() => handleTypeofQuestionOptionChange(option, index)}
                            >
                              <p className="font-medium text-grey-900 text-sm">{option.label}</p>
                              <p className="text-xs text-grey-500 mt-0.5">{option.description}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {renderTypeOfQuestionContent(index)}
                  </div>
                ))}
                
                <button
                  onClick={addNewTypeOfQuestion}
                  className="w-full py-3 border-2 border-dashed border-grey-200 rounded-xl text-sm font-medium text-grey-500 hover:text-grey-700 hover:border-grey-300 transition-colors flex items-center justify-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('createEvent.addQuestion')}
                </button>
              </div>
            )}
          </div>

          {/* FAQ Section */}
          <div className="border border-grey-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => toggleSection('faq')}
              className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-grey-25 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-grey-100 rounded-full flex items-center justify-center">
                  <span className="text-grey-500 font-semibold text-sm">?</span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-grey-900">{t('createEvent.faq')}</p>
                  <p className="text-sm text-grey-400">{t('createEvent.faqSubtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(formState?.faqList || []).filter(f => f.question).length > 0 && (
                  <span className="text-xs font-medium text-grey-600 bg-grey-100 px-2 py-1 rounded-full">
                    {formState.faqList.filter(f => f.question).length} added
                  </span>
                )}
                {expandedSections.faq ? (
                  <ChevronUpIcon className="h-5 w-5 text-grey-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-grey-400" />
                )}
              </div>
            </button>
            
            {expandedSections.faq && (
              <div className="px-6 py-6 border-t border-grey-100 bg-grey-25 space-y-4">
                {(formState?.faqList || []).map((faq, index) => (
                  <div key={index} className="bg-white rounded-xl border border-grey-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">
                        FAQ {index + 1}
                      </span>
                      {index > 0 && (
                        <button
                          onClick={() => handleDeleteFAQ(index)}
                          className="text-grey-400 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    <input
                      type="text"
                      name="question"
                      value={faq.question}
                      onChange={(e) => handleNewFAQInputChange(index, e)}
                      placeholder={t('createEvent.questionPlaceholder')}
                      className="w-full px-4 py-3 text-sm border border-grey-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-grey-300 focus:border-transparent"
                    />

                    <textarea
                      name="answer"
                      rows={2}
                      value={faq.answer}
                      onChange={(e) => handleNewFAQInputChange(index, e)}
                      placeholder={t('createEvent.answerPlaceholder')}
                      className="w-full px-4 py-3 text-sm border border-grey-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-grey-300 focus:border-transparent resize-none"
                    />
                  </div>
                ))}
                
                <button
                  onClick={addNewFAQ}
                  className="w-full py-3 border-2 border-dashed border-grey-200 rounded-xl text-sm font-medium text-grey-500 hover:text-grey-700 hover:border-grey-300 transition-colors flex items-center justify-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('createEvent.addFaq')}
                </button>
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="border border-grey-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => toggleSection('documents')}
              className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-grey-25 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-grey-100 rounded-full flex items-center justify-center">
                  <DocumentIcon className="h-5 w-5 text-grey-500" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-grey-900">{t('createEvent.eventDocuments')}</p>
                  <p className="text-sm text-grey-400">{t('createEvent.eventDocumentsSubtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(formState?.eventDocuments || []).length > 0 && (
                  <span className="text-xs font-medium text-grey-600 bg-grey-100 px-2 py-1 rounded-full">
                    {formState.eventDocuments.length} files
                  </span>
                )}
                {expandedSections.documents ? (
                  <ChevronUpIcon className="h-5 w-5 text-grey-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-grey-400" />
                )}
              </div>
            </button>
            
            {expandedSections.documents && (
              <div className="px-6 py-6 border-t border-grey-100 bg-grey-25">
                {(formState?.eventDocuments || []).length > 0 && (
                  <div className="mb-4 space-y-2">
                    {formState.eventDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-grey-200">
                        <div className="flex items-center gap-3">
                          <DocumentIcon className="h-5 w-5 text-grey-400" />
                          <span className="text-sm text-grey-700 font-medium">
                            {doc?.fileName?.length > 25 ? `${doc.fileName.slice(0, 25)}...` : doc?.fileName}
                          </span>
                        </div>
                        <button
                          onClick={() => handleFileRemove(index)}
                          className="text-grey-400 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={handleEventDocsUpload}
                  className="w-full py-8 border-2 border-dashed border-grey-200 rounded-xl hover:border-grey-300 transition-colors flex flex-col items-center gap-2"
                >
                  <UploadIcon className="h-8 w-8 text-grey-300" />
                  <span className="text-sm font-medium text-grey-600">{t('createEvent.uploadDocuments')}</span>
                  <span className="text-xs text-grey-400">{t('createEvent.uploadDocumentsTypes')}</span>
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Ticketing hint */}
        <div className="mt-10 flex items-start gap-3 p-4 bg-primary-50 border border-primary-100 rounded-xl">
          <svg className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 12v7a1 1 0 01-1 1H5a1 1 0 01-1-1v-7M16 6l-4-4-4 4M12 2v13" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-primary-800">Want to sell attendee tickets?</p>
            <p className="text-xs text-primary-600 mt-0.5">You can set up ticket types right after publishing — we'll ask you then.</p>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between pt-6">
          <button
            onClick={handleSaveForLaterClick}
            className="text-sm font-medium text-grey-400 hover:text-grey-600 transition-colors"
          >
            {t('createEvent.saveForLaterLink')}
          </button>
          <button
            onClick={handleSaveContinue}
            className="px-8 py-3 text-sm font-semibold rounded-full bg-grey-900 text-white hover:bg-grey-800 shadow-lg shadow-grey-900/20 transition-all"
          >
            {t('createEvent.continue')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateEventContent4;
